import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/rbac";
import { SchoolSelect, useSchools } from "../components/SchoolSelect";
import { localName, localFullName } from "../lib/localName";

export default function FeesPage() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const canManage = can.createInvoice(user);
  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";
  const isPersonal = isParent || isStudent;

  const staffSchools = useSchools();
  const schools = staffSchools.schools;
  const school = staffSchools.schoolId;
  const setSchool = staffSchools.setSchoolId;
  const isSuper = staffSchools.isSuper;
  const [students, setStudents] = useState([]);
  const [fees, setFees] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [openFee, setOpenFee] = useState(false);
  const [openInv, setOpenInv] = useState(false);
  const [openPay, setOpenPay] = useState(null);
  const [feeForm, setFeeForm] = useState({ name: "", amount: 0, currency: "SAR", academic_year: "2025-2026" });
  const [invForm, setInvForm] = useState({ student_id: "", fee_structure_id: "", amount: 0, academic_year: "2025-2026" });
  const [payForm, setPayForm] = useState({ amount: 0, method: "cash", reference: "" });
  const [pubSettings, setPubSettings] = useState({ payment_provider: "none", paymob_configured: false });
  const [payingInvId, setPayingInvId] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then(r => setPubSettings(r.data || {})).catch(() => {});
  }, []);
  const paymobActive = pubSettings.paymob_configured && pubSettings.payment_provider === "paymob";

  const payInvoiceWithPaymob = async (inv) => {
    setPayingInvId(inv.id);
    try {
      const { data } = await api.post("/payments/paymob/intention", {
        invoice_id: inv.id, redirection_url: window.location.href,
      });
      if (data?.checkout_url) {
        toast.success(t("redirecting_paymob") || "Redirecting to secure checkout…");
        window.location.href = data.checkout_url;
        return;
      }
      toast.error(t("paymob_no_checkout_url") || "Paymob did not return a checkout URL");
    } catch (e) {
      toast.error(e.response?.data?.detail?.paymob?.detail || e.response?.data?.detail || "Error");
    }
    setPayingInvId(null);
  };

  useEffect(() => {
    if (isPersonal) {
      if (isParent) api.get("/my/children").then(r => setStudents(r.data || []));
      else api.get("/my/student-record").then(r => { const s = r.data; if (s) setStudents([s]); });
    }
  }, [isPersonal, isParent]);

  // Payments log (staff)
  const [paymentsLog, setPaymentsLog] = useState([]);
  useEffect(() => {
    if (isPersonal || !school) return;
    api.get(`/payments/log?school_id=${school}`).then(r => setPaymentsLog(r.data || [])).catch(() => setPaymentsLog([]));
  }, [school, openPay, isPersonal]);

  useEffect(() => {
    if (isPersonal || !school) return;
    api.get(`/fee-structures?school_id=${school}`).then(r => setFees(r.data || []));
    api.get(`/students?school_id=${school}`).then(r => setStudents(r.data || []));
  }, [school, openFee, isPersonal]);

  // Invoices: backend already scopes by parent/student personal data
  useEffect(() => { api.get("/invoices").then(r => setInvoices(r.data || [])); }, [openInv, openPay]);

  const saveFee = async () => { await api.post("/fee-structures", { ...feeForm, school_id: school, amount: parseFloat(feeForm.amount) }); toast.success(t("success")); setOpenFee(false); };
  const saveInv = async () => {
    const fs = fees.find(f => f.id === invForm.fee_structure_id);
    await api.post("/invoices", { ...invForm, amount: parseFloat(invForm.amount || fs?.amount || 0) });
    toast.success(t("success")); setOpenInv(false);
  };
  const savePay = async () => {
    await api.post("/payments", { invoice_id: openPay.id, amount: parseFloat(payForm.amount), method: payForm.method, reference: payForm.reference });
    toast.success(t("success")); setOpenPay(null); setPayForm({ amount: 0, method: "cash", reference: "" });
  };

  const renderInvoicesTable = () => (
    <Card className="border shadow-none overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50"><tr>
          <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{isPersonal ? (t("student") || "Student") : "Student"}</th>
          <th className="text-end px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("amount")}</th>
          <th className="text-end px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("paid")}</th>
          <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">Status</th>
          {(canManage || (isPersonal && paymobActive)) && <th className="text-end px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("actions")}</th>}
        </tr></thead>
        <tbody>
          {invoices.map((inv, i) => {
            const outstanding = Math.max(0, (inv.amount || 0) - (inv.paid || 0));
            const canPayNow = isPersonal && paymobActive && outstanding > 0 && inv.status !== "paid";
            return (
            <tr key={inv.id} className={`border-t border-border ${i % 2 ? "bg-muted/20" : ""}`} data-testid={`inv-${inv.id}`}>
              <td className="px-4 py-3">{students.find(s => s.id === inv.student_id)?.full_name_ar || students.find(s => s.id === inv.student_id)?.full_name || inv.student_id?.slice(0, 8)}</td>
              <td className="px-4 py-3 text-end font-mono">{inv.amount}</td>
              <td className="px-4 py-3 text-end font-mono">{inv.paid || 0}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${inv.status === "paid" ? "bg-primary/10 text-primary" : inv.status === "partial" ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"}`}>{t(inv.status) || inv.status}</span></td>
              {canManage && <td className="px-4 py-3 text-end"><Button size="sm" variant="outline" onClick={() => { setOpenPay(inv); setPayForm({ ...payForm, amount: (inv.amount || 0) - (inv.paid || 0) }); }}>{t("record_payment")}</Button></td>}
              {!canManage && isPersonal && paymobActive && (
                <td className="px-4 py-3 text-end">
                  {canPayNow ? (
                    <Button size="sm" onClick={() => payInvoiceWithPaymob(inv)} disabled={payingInvId === inv.id}
                      data-testid={`pay-now-${inv.id}`}
                      className="bg-primary text-primary-foreground hover:bg-primary/90">
                      {payingInvId === inv.id ? "…" : (t("pay_now") || "ادفع الآن 💳")}
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              )}
            </tr>
          );})}
          {invoices.length === 0 && <tr><td colSpan={(canManage || (isPersonal && paymobActive)) ? 5 : 4} className="px-4 py-12 text-center text-muted-foreground">{t("no_data")}</td></tr>}
        </tbody>
      </table>
    </Card>
  );

  // ---------- Personal view: just invoices ----------
  if (isPersonal) {
    const totalDue = invoices.reduce((s, inv) => s + Math.max(0, (inv.amount || 0) - (inv.paid || 0)), 0);
    const totalPaid = invoices.reduce((s, inv) => s + (inv.paid || 0), 0);
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <h1 className="text-3xl font-semibold">{t("fees")}</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card className="p-4 border shadow-none">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("invoices")}</div>
            <div className="text-3xl font-semibold mt-1" data-testid="fees-count">{invoices.length}</div>
          </Card>
          <Card className="p-4 border shadow-none">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("paid")}</div>
            <div className="text-3xl font-semibold text-emerald-600 mt-1" data-testid="fees-paid">{totalPaid.toFixed(2)}</div>
          </Card>
          <Card className="p-4 border shadow-none">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("balance") || "Outstanding"}</div>
            <div className={`text-3xl font-semibold mt-1 ${totalDue > 0 ? "text-destructive" : "text-muted-foreground"}`} data-testid="fees-due">{totalDue.toFixed(2)}</div>
          </Card>
        </div>

        {renderInvoicesTable()}
      </div>
    );
  }

  // ---------- Staff view ----------
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-3xl font-semibold">{t("fees")}</h1>
        <SchoolSelect
          schools={schools}
          value={school}
          onChange={setSchool}
          testId="fees-school-select"
          disabled={!isSuper}
        />
      </div>

      <Tabs defaultValue="invoices" className="space-y-4">
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">{t("invoices")}</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">{t("fees")}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments-log">{t("payments_log")}</TabsTrigger>
        </TabsList>

        <TabsContent value="fees">
          {canManage && <div className="flex justify-end mb-3">
            <Dialog open={openFee} onOpenChange={setOpenFee}>
              <DialogTrigger asChild><Button disabled={!school} data-testid="new-fee-btn"><Plus className="w-4 h-4 me-2" />{t("new_fee")}</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>{t("new_fee")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t("name")}</Label><Input value={feeForm.name} onChange={e => setFeeForm({ ...feeForm, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("amount")}</Label><Input type="number" value={feeForm.amount} onChange={e => setFeeForm({ ...feeForm, amount: e.target.value })} /></div>
                    <div><Label>{t("currency")}</Label><Input value={feeForm.currency} onChange={e => setFeeForm({ ...feeForm, currency: e.target.value })} /></div>
                  </div>
                  <div><Label>{t("academic_year")}</Label><Input value={feeForm.academic_year} onChange={e => setFeeForm({ ...feeForm, academic_year: e.target.value })} /></div>
                  <Button onClick={saveFee} className="w-full" data-testid="fee-save">{t("save")}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>}
          <Card className="border shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr>
                <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("name")}</th>
                <th className="text-end px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("amount")}</th>
                <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("academic_year")}</th>
              </tr></thead>
              <tbody>
                {fees.map((f, i) => (<tr key={f.id} className={`border-t border-border ${i % 2 ? "bg-muted/20" : ""}`}><td className="px-4 py-3">{f.name}</td><td className="px-4 py-3 text-end font-mono font-semibold">{f.amount} {f.currency}</td><td className="px-4 py-3 font-mono text-muted-foreground">{f.academic_year}</td></tr>))}
                {fees.length === 0 && <tr><td colSpan={3} className="px-4 py-12 text-center text-muted-foreground">{t("no_data")}</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          {canManage && <div className="flex justify-end mb-3">
            <Dialog open={openInv} onOpenChange={setOpenInv}>
              <DialogTrigger asChild><Button disabled={!school || fees.length === 0} data-testid="new-invoice-btn"><Plus className="w-4 h-4 me-2" />{t("new_invoice")}</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>{t("new_invoice")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t("students")}</Label>
                    <Select value={invForm.student_id} onValueChange={v => setInvForm({ ...invForm, student_id: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{localFullName(s, lang)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("fees")}</Label>
                    <Select value={invForm.fee_structure_id} onValueChange={v => { const f = fees.find(x => x.id === v); setInvForm({ ...invForm, fee_structure_id: v, amount: f?.amount || 0 }); }}><SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{fees.map(f => <SelectItem key={f.id} value={f.id}>{f.name} ({f.amount})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("amount")}</Label><Input type="number" value={invForm.amount} onChange={e => setInvForm({ ...invForm, amount: e.target.value })} /></div>
                  <Button onClick={saveInv} className="w-full" data-testid="invoice-save">{t("save")}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>}
          {renderInvoicesTable()}

          <Dialog open={!!openPay} onOpenChange={(o) => !o && setOpenPay(null)}>
            <DialogContent><DialogHeader><DialogTitle>{t("record_payment")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("amount")}</Label><Input type="number" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} /></div>
                <div><Label>{t("method")}</Label>
                  <Select value={payForm.method} onValueChange={v => setPayForm({ ...payForm, method: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cash">{t("cash")}</SelectItem><SelectItem value="bank">{t("bank")}</SelectItem><SelectItem value="pos">{t("pos")}</SelectItem><SelectItem value="online">{t("online")}</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Reference</Label><Input value={payForm.reference} onChange={e => setPayForm({ ...payForm, reference: e.target.value })} /></div>
                <Button onClick={savePay} className="w-full" data-testid="pay-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted/50"><tr>
                  <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("date")}</th>
                  <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("student")}</th>
                  <th className="text-end px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("amount")}</th>
                  <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("method")}</th>
                  <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("reference_label")}</th>
                  <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("recorded_by")}</th>
                </tr></thead>
                <tbody>
                  {paymentsLog.map((p, i) => (
                    <tr key={p.id} className={`border-t border-border ${i % 2 ? "bg-muted/20" : ""}`} data-testid={`paylog-${p.id}`}>
                      <td className="px-4 py-3 font-mono text-muted-foreground whitespace-nowrap">{p.at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-3">{p.student_name || "—"}</td>
                      <td className="px-4 py-3 text-end font-mono font-semibold text-emerald-600">{p.amount}</td>
                      <td className="px-4 py-3">{t(p.method) || p.method}</td>
                      <td className="px-4 py-3 font-mono text-muted-foreground">{p.reference || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{p.by_name || "—"}</td>
                    </tr>
                  ))}
                  {paymentsLog.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">{t("no_data")}</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
